import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  RadioTower,
  Radar as RadarIcon,
  PlaneLanding,
  Wrench,
  CalendarRange,
  Moon,
  Compass,
  LogOut,
  Settings,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CalendarClock } from "lucide-react";

const NAV = [
  { to: "/app/tower", label: "Tower", code: "T-01", icon: RadioTower },
  { to: "/app/radar", label: "Radar", code: "R-02", icon: RadarIcon },
  { to: "/app/runway", label: "Runway", code: "R-03", icon: PlaneLanding },
  { to: "/app/hangar", label: "Hangar", code: "H-04", icon: Wrench },
  { to: "/app/planner", label: "Planner", code: "P-05", icon: CalendarRange },
  { to: "/app/nest", label: "Nest", code: "N-06", icon: Moon },
];

const statusColor = (s) =>
  s === "green" ? "text-signalGreen" : s === "red" ? "text-signalRed" : "text-signalAmber";

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [signals, setSignals] = useState(null);
  const [calStatus, setCalStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/signals")
      .then((r) => !cancelled && setSignals(r.data))
      .catch(() => {});
    api
      .get("/calendar/status")
      .then((r) => !cancelled && setCalStatus(r.data))
      .catch(() => !cancelled && setCalStatus({ connected: false, configured: false }));
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const initials = (user?.name || user?.email || "?")
    .split(/[ .@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <div data-testid="app-shell" className="flex min-h-screen bg-obsidian text-ink">
      {/* Sidebar */}
      <aside className="hidden w-64 flex-col border-r border-rule bg-deep/40 md:flex">
        <Link to="/" className="flex items-center gap-2.5 border-b border-rule px-6 py-5">
          <span className="relative flex h-6 w-6 items-center justify-center border border-teal/70">
            <Compass className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            <span className="absolute -top-px -left-px h-1 w-1 bg-amber" />
          </span>
          <span className="font-display text-lg text-ink">Life OS</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-mist">
            preview
          </span>
        </Link>

        <nav className="flex-1 px-3 py-6">
          <p className="px-3 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Instruments
          </p>
          <ul className="mt-4 space-y-1">
            {NAV.map(({ to, label, code, icon: Icon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  data-testid={`nav-${label.toLowerCase()}`}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center gap-3 border-l-2 px-4 py-3 transition-colors duration-300",
                      isActive
                        ? "border-teal bg-deep text-ink"
                        : "border-transparent text-mist hover:border-rule hover:text-ink"
                    )
                  }
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  <span className="flex-1 font-mono text-[11px] uppercase tracking-[0.22em]">
                    {label}
                  </span>
                  <span className="font-mono text-[10px] text-mist/70">{code}</span>
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="mt-8 border-t border-rule pt-4">
            <NavLink
              to="/app/household"
              data-testid="nav-household"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300",
                  isActive ? "text-ink" : "text-mist hover:text-ink"
                )
              }
            >
              <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
              Household
            </NavLink>
            {(user?.household_role === "owner" || user?.household_role === "co_owner") && (
              <NavLink
                to="/app/onboarding"
                data-testid="nav-onboarding"
                className="flex items-center gap-3 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:text-ink"
              >
                <Settings className="h-3.5 w-3.5" strokeWidth={1.5} />
                Edit profile
              </NavLink>
            )}
            {user?.is_admin && (
              <NavLink
                to="/app/admin/waitlist"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300",
                    isActive ? "text-ink" : "text-mist hover:text-ink"
                  )
                }
              >
                <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                Admin · Manifest
              </NavLink>
            )}
          </div>
        </nav>

        {/* User + logout */}
        <div className="border-t border-rule px-4 py-4">
          <div className="mb-3 flex items-center gap-3" data-testid="user-panel">
            {user?.picture ? (
              <img
                src={user.picture}
                alt=""
                className="h-8 w-8 border border-rule object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center border border-rule bg-obsidian font-mono text-[11px] text-teal">
                {initials || "??"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-ink">{user?.name || "Signed in"}</div>
              <div className="truncate font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                {user?.email}
              </div>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="nav-logout-btn"
            className="flex w-full items-center justify-center gap-2 border border-rule px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-amber hover:text-ink"
          >
            <LogOut className="h-3 w-3" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-testid="app-topbar"
          className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-rule bg-obsidian/85 px-6 backdrop-blur-xl md:px-8"
        >
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
              <span className={cn("h-1.5 w-1.5 animate-pulse-soft",
                signals?.capacity_status === "red"
                  ? "bg-signalRed"
                  : signals?.capacity_status === "amber"
                    ? "bg-signalAmber"
                    : "bg-signalGreen")} />
              Household · {signals?.outlook?.split(" ")[0] || "Reading…"}
            </div>
            <div className="hidden items-center gap-6 md:flex">
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Capacity</span>
                <span className={cn("font-mono text-sm", statusColor(signals?.capacity_status))} data-testid="topbar-capacity">
                  {signals ? `${signals.capacity_load}%` : "—"}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Compression</span>
                <span className={cn("font-mono text-sm", statusColor(signals?.compression_status))} data-testid="topbar-compression">
                  {signals ? `${signals.compression_index}%` : "—"}
                </span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">Recovery</span>
                <span className={cn("font-mono text-sm", statusColor(signals?.recovery_status))} data-testid="topbar-recovery">
                  {signals ? `${signals.recovery_margin}%` : "—"}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            <span className="hidden sm:inline">Local · 034° · Nominal</span>
            {calStatus?.connected ? (
              <span
                data-testid="topbar-calendar-connected"
                className="inline-flex items-center gap-1 border border-signalGreen/50 px-2 py-1 text-signalGreen"
                title={`Calendar: ${calStatus.email}`}
              >
                <CalendarClock className="h-3 w-3" strokeWidth={1.5} />
                Cal · Live
              </span>
            ) : calStatus?.configured ? (
              <button
                onClick={() => navigate("/app/radar")}
                data-testid="topbar-calendar-disconnected"
                className="inline-flex items-center gap-1 border border-rule px-2 py-1 text-mist hover:border-teal hover:text-ink"
              >
                <CalendarClock className="h-3 w-3" strokeWidth={1.5} />
                Cal · Off
              </button>
            ) : null}
            <span className="rounded-full border border-rule px-2.5 py-1">Preview</span>
          </div>
        </header>

        <main className="flex-1">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav className="sticky bottom-0 z-30 grid grid-cols-6 gap-px border-t border-rule bg-deep md:hidden">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center justify-center gap-1 py-3 text-[10px]",
                  isActive ? "bg-obsidian text-ink" : "text-mist"
                )
              }
            >
              <Icon className="h-4 w-4" strokeWidth={1.5} />
              <span className="font-mono uppercase tracking-[0.16em]">{label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
